import { FC, useMemo, useState } from "react";
import api from "api/landlord/applications";
import { VerificationItem } from "./components/VerificationItem";
import { ReportHeader } from "../../components/ReportHeader";
import { ApplicantType } from "shared/types/application";
import { DocumentStatus, KycCheckStatus, PlaidReport } from "./types";
import { Section } from "views/applications-id-report/components/Section";
import {
  BarChart02,
  FaceID,
  FileX03,
  Inbox01,
  User03,
  UserSquare
} from "untitledui-js-base";
import { Loader } from "shared/components/Loader";
import { useQuery } from "@tanstack/react-query";
import { VerificationReport } from "types/landlord/reports";
import { Summary } from "./components/Summary";
import { cx, formatPhone } from "shared/utils";
import moment from "moment";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { AssetItem } from "views/applications-id-report/components/SmartRender/AssetItem";
import { Row } from "views/applications-id-report/components/Row";
import { PhysicalDocumentCategoryReadable } from "./constants";
import { CheckmarkChime } from "./components/CheckmarkChime";
import { Tabs } from "shared/components/Tabs";
import { GenericBadge } from "./components/GenericBadge";
import SuccessIcon from "shared/icons/status-icons/success-icon.svg?react";
import CloseIcon from "shared/icons/status-icons/fail-icon.svg?react";
import { BadgeSize, Badge, BadgeVariant, Show } from "shared/components";
import { getVerificationQueryKeys } from "../../utils/getVerificationQueryKeys";
import { VerificationNames } from "shared/types";
import { IdentityVerificationSettings } from "types/landlord/verificationTemplate";
import { PrintReady } from "views/applications-id-report/providers/PrintProvider";
import { VerificationStatus } from "shared/types";
import { getFullName } from "shared/utils/get-full-name";
import { Applicant } from "types/application";
import { OverrideIdentityModal } from "./components/OverrideIdentityModal";

const DynamicIdDocument = ({
  url,
  fallbackUrl,
  name
}: {
  url?: string;
  fallbackUrl?: string;
  name: string;
}) => {
  const [viewMode, setViewMode] = useState("url");

  return (
    <div
      className={cx(
        "border border-alpha-100 rounded-lg overflow-hidden w-[264px]",
        viewMode === "error" && "bg-background-900"
      )}
    >
      {viewMode === "error" ? (
        <div className="py-5">
          <FileX03 className="mx-auto h-6 w-6 stroke-content-700" />
        </div>
      ) : (
        <img
          src={viewMode === "fallbackUrl" ? fallbackUrl : url}
          alt="Front of document"
          onError={() => {
            setViewMode((prev) => (prev === "url" ? "fallbackUrl" : "error"));
          }}
          className="h-auto w-[264px]"
        />
      )}

      <p className="py-3 bg-white border-t text-center border-alpha-100 text-xs font-medium text-content-1000">
        {name}
      </p>
    </div>
  );
};

interface IdentityVerificationReportSliceProps {
  applicationId?: string;
  applicant: Applicant;
  verificationId: number;
  templateIdentityVerificationSettings: IdentityVerificationSettings;
  requiresOverridesIdentityVerification: boolean;
}

export const IdentityVerificationReportSlice: FC<
  IdentityVerificationReportSliceProps
> = ({
  applicationId,
  applicant,
  verificationId,
  templateIdentityVerificationSettings,
  requiresOverridesIdentityVerification
}) => {
  const [tab, setTab] = useState(1);
  const [overrideIdentityModalOpen, setOverrideIdentityModalOpen] =
    useState(false);

  const { data: verification, isLoading } = useQuery<
    VerificationReport<PlaidReport>
  >({
    queryKey: getVerificationQueryKeys({
      reportName: VerificationNames.IdentityVerification,
      applicantId: applicant.id,
      applicationId: applicant.application_id,
      verificationId
    }),
    queryFn: () =>
      api
        .getApplicantVerification(
          applicant.application_id,
          applicant.id,
          verificationId
        )
        .then((d) => d.verification)
  });

  const checkSuccessStatus = (status?: string) =>
    ["success", "manually_approved"].includes(status ?? "");

  const isSelfieCheckEnabled = Boolean(
    templateIdentityVerificationSettings[applicant.role as ApplicantType]
      .identity_verification?.selfie_check
  );

  const documentAttemptsTabDisplayMap = useMemo(() => {
    const ret = {};

    verification?.report.documents?.forEach((doc) => {
      ret[`Attempt ${doc.attempt}`] = (
        <div className="flex items-center gap-2">
          {checkSuccessStatus(doc.status) ? (
            <SuccessIcon className="text-success-500 h-4 w-4" />
          ) : (
            <CloseIcon className="text-danger-500 h-4 w-4" />
          )}
          <p
            className={cx(
              "text-sm",
              tab === doc.attempt
                ? "font-bold text-primary-500"
                : "text-content-400"
            )}
          >
            {`Attempt ${doc.attempt}`}
          </p>
        </div>
      );
    });
    return ret;
  }, [verification, tab]);

  if (isLoading)
    return (
      <>
        <ReportHeader
          title="Identity Verification"
          reportDate=""
          verificationId={0}
        />
        <Loader />
      </>
    );

  if (!verification) return null;

  const activeDocument = verification.report.documents?.find(
    (d) => d.attempt === tab
  );

  const activeSelfieCheck = verification.report.selfie_check?.selfies
    .sort((a, b) => b.status.localeCompare(a.status))
    .find((d) => ["success", "failed"].includes(d.status));

  const successFullDocument = verification.report.documents?.find(
    (d) => d.status === "success"
  );

  const verificationStatus =
    [
      verification.report.kyc_check_status,
      verification.report.selfie_check_status
    ].includes("manually_approved") ||
    verification.report.documents?.some(
      (doc) => doc.status === DocumentStatus.ManuallyApproved
    )
      ? VerificationStatus.ManuallyApproved
      : verification.status;

  return (
    <div className="bg-white">
      <ReportHeader
        title="Identity verification"
        reportDate={verification.completed_at}
        verificationId={verificationId}
      />

      <PrintReady verificationId={verificationId} />

      <Section
        sectionTitle="Identity verification summary"
        icon={<BarChart02 size="20px" className="stroke-content-500" />}
        maxWidth="100%"
      >
        <Summary
          fullName={
            verification.report.first_name
              ? getFullName({
                  firstName: verification.report.first_name,
                  lastName: verification.report.last_name,
                  middleName: verification.report.middle_name,
                  short: true
                })
              : "--"
          }
          overriddenBy={verification.overridden_by}
          verificationStatus={verificationStatus as VerificationStatus}
          sessionId={verification.report.session_id || "--"}
          ipAddress={verification.report.ip_address || "--"}
          isDocumentCheckSuccess={Boolean(
            verification.report.documents?.some((d) =>
              checkSuccessStatus(d.status)
            )
          )}
          isRecordCheckSuccess={checkSuccessStatus(
            verification.report.kyc_check?.status
          )}
          isSelfieCheckSuccess={checkSuccessStatus(
            verification.report.selfie_check_status
          )}
          isSelfieCheckEnabled={isSelfieCheckEnabled}
          openOverrideIdentityModal={
            requiresOverridesIdentityVerification
              ? () => setOverrideIdentityModalOpen(true)
              : undefined
          }
        />
      </Section>

      <Section
        sectionTitle="Self-reported information"
        icon={<User03 className="stroke-content-500" size="20" />}
      >
        <ContentTitle title="Basic information">
          <div className="flex flex-col gap-2">
            <Row
              title="Full name"
              value={
                verification.report.first_name
                  ? getFullName({
                      firstName: verification.report.first_name,
                      lastName: verification.report.last_name,
                      middleName: verification.report.middle_name,
                      short: true
                    })
                  : "-"
              }
            />
            <Row title="Phone" value={formatPhone(verification.report.phone)} />
            <Row
              title="Birthday"
              value={
                verification.report.dob &&
                moment(verification.report.dob).isValid()
                  ? moment(verification.report.dob).format("ll")
                  : "-"
              }
            />
            <Row title="SSN" value={verification.report.ssn} />
          </div>
        </ContentTitle>
        <ContentTitle title="Address">
          <div className="flex flex-col gap-2">
            <Row title="Street address" value={verification.report.address1} />
            <Row
              title="Apartment, suite, etc."
              value={verification.report.address2}
            />
            <Row title="City" value={verification.report.city} />
            <Row
              title="State / Province / Region"
              value={verification.report.state}
            />
            <Row title="Zip / Postal code" value={verification.report.zip} />
            <Row title="Country" value={verification.report.country} />
          </div>
        </ContentTitle>
      </Section>
      {verification.report.kyc_check_status ===
      KycCheckStatus.NotApplicable ? null : (
        <Section
          sectionTitle="Records match verification"
          sectionClassName="pt-2 pb-0 md:p-6 pt-3 pl-4 pb-12"
          rightElement={
            [KycCheckStatus.Success, KycCheckStatus.ManuallyApproved].includes(
              verification.report.kyc_check_status ??
                KycCheckStatus.NotApplicable
            ) ? (
              <Badge
                size={BadgeSize.BASE}
                variant={BadgeVariant.SUCCESS}
                className="items-center border border-alpha-100"
                leftElement={
                  <SuccessIcon className="text-success-500 h-4 w-4" />
                }
              >
                Passed{" "}
                {verification.report.kyc_check_status ===
                KycCheckStatus.ManuallyApproved
                  ? "(Manually overridden)"
                  : ""}
              </Badge>
            ) : (
              <Badge
                size={BadgeSize.BASE}
                variant={BadgeVariant.ERROR}
                className="items-center border border-alpha-100"
                leftElement={<CloseIcon className="text-danger-500 h-4 w-4" />}
              >
                Failed
              </Badge>
            )
          }
          tooltip={
            <>
              <p className="font-bold">Records match verification</p>
              <p>
                Records match verification verifies a user&apos;s name, address,
                date of birth, phone number, and ID number (such as SSN) against
                available records
              </p>
            </>
          }
          icon={<Inbox01 size="20px" className="stroke-content-400" />}
        >
          <VerificationItem
            label="Name"
            value={getFullName({
              firstName: verification.report.first_name,
              lastName: verification.report.last_name,
              middleName: verification.report.middle_name,
              short: true
            })}
            status={verification.report.name_summary}
          />
          <VerificationItem
            label="Date of birth"
            value={
              verification.report.dob &&
              moment(verification.report.dob).isValid()
                ? moment(verification.report.dob).format("ll")
                : "--"
            }
            status={verification.report.dob_summary}
          />
          <VerificationItem
            label="Phone number"
            value={formatPhone(verification.report.phone)}
            status={verification.report.phone_summary}
          />
          <VerificationItem
            label="Address"
            value={verification.report.address1}
            status={verification.report.address_summary}
          />
          <VerificationItem
            label="Address type"
            value={String(verification.report.kyc_check?.address.type || "")}
            poBox={verification.report.po_box}
          />
          <VerificationItem
            label="Tax ID number (SSN)"
            value={verification.report.ssn}
            status={verification.report.ssn_summary}
          />
        </Section>
      )}

      <Section
        sectionTitle="Document verification"
        rightElement={
          verification.report.documents?.some((d) =>
            [DocumentStatus.Success, DocumentStatus.ManuallyApproved].includes(
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              d.status ?? DocumentStatus.Failed
            )
          ) ? (
            <Badge
              size={BadgeSize.BASE}
              variant={BadgeVariant.SUCCESS}
              className="items-center border border-alpha-100"
              leftElement={<SuccessIcon className="text-success-500 h-4 w-4" />}
            >
              Passed{" "}
              {verification.report.documents.some(
                (d) => d.status === DocumentStatus.ManuallyApproved
              )
                ? "(Manually overridden)"
                : ""}
            </Badge>
          ) : (
            <Badge
              size={BadgeSize.BASE}
              variant={BadgeVariant.ERROR}
              className="items-center border border-alpha-100"
              leftElement={<CloseIcon className="text-danger-500 h-4 w-4" />}
            >
              Failed
            </Badge>
          )
        }
        tooltip={
          <>
            <p className="font-bold">Document Verification</p>
            <p>
              Document Verification prompts the user to upload an image of an
              identity document. Plaid will use anti-fraud checks to verify that
              the document appears to be legitimate and is unexpired. Plaid will
              also verify the date of birth and name on the document against the
              data provided by the user.
            </p>
          </>
        }
        icon={<UserSquare size="20px" className="stroke-content-400" />}
      >
        <Tabs
          variant="underline"
          setActiveTab={(tab) => setTab(Number(tab.replaceAll("Attempt ", "")))}
          selected={`Attempt ${tab}`}
          displayMap={documentAttemptsTabDisplayMap}
          tabs={
            verification.report.documents?.map(
              (doc) => `Attempt ${doc.attempt}`
            ) || []
          }
        />

        {activeDocument ? (
          <div className="mt-8">
            <ContentTitle title="Documents">
              <div className="flex gap-4 flex-wrap">
                <DynamicIdDocument
                  name="Front of document"
                  url={activeDocument.assets?.cropped_front?.asset?.url || ""}
                  fallbackUrl={
                    activeDocument.assets?.original_front?.asset?.url || ""
                  }
                />
                {activeDocument.extracted_data?.category ===
                "passport" ? null : (
                  <DynamicIdDocument
                    name="Back of document"
                    url={activeDocument.assets?.cropped_back?.asset?.url || ""}
                    fallbackUrl={
                      activeDocument.assets?.original_back?.asset?.url || ""
                    }
                  />
                )}
              </div>
            </ContentTitle>

            <ContentTitle
              title="Extracted information"
              tooltip={
                <>
                  <p className="font-bold">Extracted information</p>
                  <p>Data extracted from a user-submitted document</p>
                </>
              }
            >
              <div className="flex flex-col gap-2">
                <Row
                  title="Extracted document type"
                  value={
                    PhysicalDocumentCategoryReadable[
                      activeDocument.extracted_data?.category || ""
                    ] || "--"
                  }
                />

                <Row
                  title="Country of issue"
                  value={activeDocument.extracted_data?.issuing_country}
                />

                <Row
                  title="Region of issue"
                  value={activeDocument.extracted_data?.issuing_region}
                />

                <Row
                  title="Date of birth"
                  value={
                    activeDocument.extracted_data?.date_of_birth &&
                    moment(
                      activeDocument.extracted_data.date_of_birth
                    ).isValid()
                      ? moment(
                          activeDocument.extracted_data.date_of_birth
                        ).format("ll")
                      : "--"
                  }
                />

                <Row
                  title="Document number"
                  value={activeDocument.extracted_data?.id_number}
                />

                <Row
                  title="Expiration date"
                  value={
                    activeDocument.extracted_data?.expiration_date &&
                    moment(
                      activeDocument.extracted_data.expiration_date
                    ).isValid()
                      ? moment(
                          activeDocument.extracted_data.expiration_date
                        ).format("ll")
                      : "--"
                  }
                />
              </div>
            </ContentTitle>

            <ContentTitle
              title="Fraud analysis"
              tooltip={
                <>
                  <p className="font-bold">Fraud analysis</p>
                  <p>
                    High level descriptions of how the associated document was
                    processed. If a document fails verification, the details in
                    the analysis object should help clarify why the document was
                    rejected
                  </p>
                </>
              }
            >
              <div className="flex flex-col gap-2.5">
                <Row
                  title="Authenticity"
                  value={
                    <GenericBadge
                      value={activeDocument.analysis.authenticity}
                    />
                  }
                />

                <Row
                  title="Image quality"
                  value={
                    <GenericBadge
                      value={activeDocument.analysis.image_quality}
                    />
                  }
                />

                <Row
                  title="Name"
                  value={
                    <GenericBadge
                      value={activeDocument.analysis.extracted_data?.name}
                    />
                  }
                />

                <Row
                  title="Date of birth"
                  value={
                    <GenericBadge
                      value={
                        activeDocument.analysis.extracted_data?.date_of_birth
                      }
                    />
                  }
                />

                <Row
                  title="Expiration date"
                  value={
                    <GenericBadge
                      value={
                        activeDocument.analysis.extracted_data?.expiration_date
                      }
                    />
                  }
                />

                <Row
                  title="Issuing country"
                  value={
                    <GenericBadge
                      value={
                        activeDocument.analysis.extracted_data?.issuing_country
                      }
                    />
                  }
                />
              </div>
            </ContentTitle>
          </div>
        ) : null}
      </Section>

      {isSelfieCheckEnabled ? (
        <Section
          sectionTitle="Selfie check"
          icon={<FaceID size="20px" className="stroke-content-400" />}
          rightElement={
            checkSuccessStatus(verification.report.selfie_check_status) ? (
              <Badge
                size={BadgeSize.BASE}
                variant={BadgeVariant.SUCCESS}
                className="items-center border border-alpha-100"
                leftElement={
                  <SuccessIcon className="text-success-500 h-4 w-4" />
                }
              >
                Passed{" "}
                {verification.report.selfie_check_status === "manually_approved"
                  ? "(Manually overridden)"
                  : ""}
              </Badge>
            ) : (
              <Badge
                size={BadgeSize.BASE}
                variant={BadgeVariant.ERROR}
                className="items-center border border-alpha-100"
                leftElement={<CloseIcon className="text-danger-500 h-4 w-4" />}
              >
                Failed
              </Badge>
            )
          }
          tooltip={
            <>
              <p className="font-bold">Selfie check</p>
              <p>
                Selfie check verifies that the submitted image is a live video
                of a real human. It also confirms that the selfie matches the
                photo in the document.
              </p>
            </>
          }
        >
          <div className="flex items-center flex-col md:flex-row">
            <div className="max-w-[200px]">
              <AssetItem
                content_type={
                  activeSelfieCheck?.assets?.image_url?.asset?.content_type ||
                  activeSelfieCheck?.assets?.video_url?.asset?.content_type ||
                  activeSelfieCheck?.assets?.liveness?.asset?.content_type ||
                  ""
                }
                filename="Liveness"
                url={
                  activeSelfieCheck?.assets?.image_url?.asset?.url ||
                  activeSelfieCheck?.assets?.video_url?.asset?.url ||
                  activeSelfieCheck?.assets?.liveness?.asset?.url ||
                  ""
                }
              />
            </div>

            <div className="w-[116px] shrink-0">
              <div className="flex items-center">
                <div className="w-full h-px border-t border-dashed border-alpha-200" />

                <CheckmarkChime
                  isSuccess={checkSuccessStatus(
                    verification.report.selfie_check_status
                  )}
                />

                <div className="w-full h-px border-t border-dashed border-alpha-200" />
              </div>

              <p className="text-xs font-medium mt-2 text-content-1000 max-w-[84px] mx-auto text-center">
                {checkSuccessStatus(verification.report.selfie_check_status)
                  ? "Matches documents"
                  : "Does not match documents"}
              </p>
            </div>

            <div className="max-w-[200px]">
              <AssetItem
                content_type={
                  successFullDocument?.assets?.face?.asset?.content_type || ""
                }
                filename="Photo from document"
                url={successFullDocument?.assets?.face?.asset?.url || ""}
              />
            </div>
          </div>
        </Section>
      ) : null}
      <Show if={overrideIdentityModalOpen}>
        <OverrideIdentityModal
          open={overrideIdentityModalOpen}
          onClose={() => setOverrideIdentityModalOpen(false)}
          apiEndpoint={`/portal/v1/applications/${applicant.application_id}/applicants/${applicant.id}/override_identity`}
          invalidateKeys={[
            getVerificationQueryKeys({
              reportName: VerificationNames.IdentityVerification,
              applicantId: applicant.id,
              applicationId: applicant.application_id,
              verificationId
            }),
            ["APPLICATION_ID_REPORT", applicationId]
          ]}
        />
      </Show>
    </div>
  );
};
